import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import { DriverWorkShiftEdit } from "@/models/driverWorkShiftsAndCycle";
import { createDriverWorkShiftAPI } from "@/util/api/driverWorkShifts";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "@/components/hoc/SnackbarAlert";
import { Messages } from "@/util/Messages";
import { DriverWorkShiftEditTemplate } from "@/components/driverWorkCycles/DriverWorkShiftEditTemplate";
import { DriverWorkCyclesConstants } from "./DriverWorkCyclesConstants";
import { getOfficeAPI } from "@/util/api/API";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";

export const DriverWorkShiftCreate = () => {
  const params = useParams<{ office_id: string }>();
  const officeId = params.office_id;

  const { navigate } = useStatefulNavigator();
  const goToDetail = useCallback(() => {
    navigate(`/driver_work_cycles/${officeId}`);
  }, [navigate, officeId]);

  const [driverWorkShift, setDriverWorkShift] = useState<DriverWorkShiftEdit>({
    id: undefined,
    isDayOff: false,
    version: undefined,
    isMultipleHolidayVersionRequest: true,
  });

  const [loading, createDriverWorkShiftAPIWrapper] = useLoadingPromise(
    createDriverWorkShiftAPI
  );

  const handleCreate = useCallback(() => {
    if (officeId == null || loading) {
      return;
    }

    const {
      weekdayIsDayOff,
      weekdayOutgoingBin,
      weekdayIncomingBin,
      holidayIsDayOff,
      holidayOutgoingBin,
      holidayIncomingBin,
      ...request
    } = driverWorkShift;

    createDriverWorkShiftAPIWrapper({
      ...request,
      weekdayIsDayOff: !!weekdayIsDayOff,
      weekdayOutgoingBin: !weekdayIsDayOff ? weekdayOutgoingBin : undefined,
      weekdayIncomingBin: !weekdayIsDayOff ? weekdayIncomingBin : undefined,
      holidayIsDayOff: !!holidayIsDayOff,
      holidayOutgoingBin: !holidayIsDayOff ? holidayOutgoingBin : undefined,
      holidayIncomingBin: !holidayIsDayOff ? holidayIncomingBin : undefined,
      officeId: Number(officeId),
    })
      .then(goToDetail)
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([404, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Register}${Messages.ErrorOccurred}: ${
              e?.response?.data?.message ?? ""
            }`
          );
        }
      });
  }, [
    createDriverWorkShiftAPIWrapper,
    driverWorkShift,
    goToDetail,
    loading,
    officeId,
  ]);

  useEffect(() => {
    if (officeId == null) {
      return;
    }

    getOfficeAPI(Number(officeId))
      .then((res) =>
        setDriverWorkShift({
          ...driverWorkShift,
          offices: res.data,
        })
      )
      .catch((e) =>
        console.log("===Error occurred while getting office===", e)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeId]);

  return (
    <DriverWorkShiftEditTemplate
      title={DriverWorkCyclesConstants.DriverWorkShiftCreate}
      driverWorkShift={driverWorkShift}
      loading={loading}
      onCancel={goToDetail}
      onSubmit={handleCreate}
      onChangeData={setDriverWorkShift}
    />
  );
};
